<template>
  <div style="gap: 0px">
    <RichTextRenderer
      :document="text"
      :node-renderers="nodesRenderer"
      :mark-renderers="markRenderers"
    />
  </div>
</template>

<script setup lang="ts">
import RichTextRenderer, {
  type MarksRenderer,
  type NodesRenderer
} from 'contentful-rich-text-vue-renderer';
import type { Block, Document, Text } from '@contentful/rich-text-types';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { TableBlock } from '@hypercodestudio/basler-components';
import isChildOf from '~/utils/richtext/isChildOf';
import { createSplitParagraphRenderer } from '~/utils/richtext/createSplitParagraphRenderer';
import { isEmpty } from '~/utils/isEmpty';
import { useGlobalH2Index } from '~/composables/useGlobalH2Index';
import { isContentPage } from '~/utils/guards/isContentPage';
import type { IContentPage } from '~~/lib/ContentfulService';

const AppFormula = defineAsyncComponent(
  () => import('~/components/global/lib/AppFormula.vue')
);
const Link = defineAsyncComponent(
  () => import('~/components/global/contentful/Link.vue')
);
const MappedComponent = defineAsyncComponent(
  () => import('~/components/global/MappedComponent.vue')
);
// Vue runtime functions like h() are auto-imported in Nuxt

interface Props {
  richText: Document;
  nodesRenderer?: NodesRenderer;
  hasLightVerticalHeader?: boolean;
  cols?: number;
  colsCentered?: boolean;
  contentCentered?: boolean;
}

const props = defineProps<Props>();

// Stable H2 ids across documents / re-renders (must match extractTocItems)
const { getIdForNode } = useGlobalH2Index();

const convertNewlinesToBreaks = (text: string) => {
  const parts = text.split('\n');
  return parts.flatMap((part, index) => {
    return index < parts.length - 1 ? [part, h('br')] : [part];
  });
};

/* DBP-2495 Workaround to remove empty paragraph*/
const text = computed(() => {
  const content = props.richText;

  const lastContent = content.content[content.content.length - 1];
  if (lastContent && lastContent.content && lastContent.content.length === 1) {
    const lastNode = lastContent.content[0];

    // Type guard to check if the node is a Text node
    if (
      lastNode.nodeType === 'text' &&
      'value' in lastNode &&
      isEmpty((lastNode as Text).value)
    ) {
      delete content.content[content.content.length - 1];
    }
  }

  return content;
});

const markRenderers: MarksRenderer = {
  [MARKS.STRIKETHROUGH]: (text: Text | string[], key, _next) => {
    return h(
      'span',
      { style: 'text-decoration: line-through;', key },
      text as unknown as string
    );
  }
};

const isMathematicalFormulaEntry = (node: any) =>
  node?.data?.target?.sys?.contentType?.sys?.id === 'mathematicalFormula';

const getFormulaProps = (node: any, isBlock: boolean) => ({
  latex: node?.data?.target?.fields?.laTeXCode,
  spokenText: node?.data?.target?.fields?.spokenText,
  isBlock
});

const nodesRenderer: NodesRenderer = {
  ...(props.nodesRenderer ?? {}),
  [BLOCKS.PARAGRAPH]: createSplitParagraphRenderer(),
  [BLOCKS.EMBEDDED_ENTRY]: (node, key) => {
    if (isMathematicalFormulaEntry(node)) {
      return h(AppFormula, { key, ...getFormulaProps(node, true) });
    }

    return h(MappedComponent, {
      key,
      item: node.data.target
    });
  },
  [INLINES.EMBEDDED_ENTRY]: (node, key) => {
    if (isMathematicalFormulaEntry(node)) {
      return h(AppFormula, { key, ...getFormulaProps(node, false) });
    }

    return h(MappedComponent, {
      key,
      item: node.data.target
    });
  },
  [INLINES.ENTRY_HYPERLINK]: (node, key, next) => {
    if (isContentPage(node.data.target)) {
      const slug = (node.data.target as unknown as IContentPage).fields?.slug;
      if (slug) {
        return h(
          'a',
          {
            key,
            href: buildUrlString(node.data.target.sys.locale, slug),
            target: '_self'
          },
          next?.(node.content as Block[], key, next)
        );
      }
    }

    return h(
      Link,
      {
        key,
        item: node.data?.target,
        useSlot: true
      },
      {
        default: () => next?.(node.content as Block[], key, next)
      }
    );
  },
  [INLINES.ASSET_HYPERLINK]: (node, key, next) => {
    return h(
      'a',
      {
        key,
        href: node.data.target.fields?.file?.url,
        download: true,
        target: '_blank'
      },
      next?.(node.content as Block[], key, next)
    );
  },
  // H2 ids for TOC — must match extractTocItems() (getIdForNode is idempotent)
  [BLOCKS.HEADING_2]: (node, key, next) => {
    return h(
      'h2',
      {
        key,
        id: getIdForNode(node)
      },
      next?.(node.content as Block[], key, next)
    );
  },
  [BLOCKS.TABLE_HEADER_CELL]: (node, key, next) => {
    return h(
      'th',
      { key, scope: 'col' },
      next?.(node.content as Block[], key, next)
    );
  },
  [BLOCKS.OL_LIST]: (node, key, next) => {
    return h(
      'ol',
      {
        class: `level-${
          ((key?.replace('RichText--', '').split('-').length ?? 1) + 1) / 2
        }`
      },
      next?.(node.content as Block[], key, next)
    );
  },
  [BLOCKS.TABLE]: (node, key, next) => {
    const tableRows = node.content as Block[];

    if (!tableRows || tableRows.length === 0) {
      return;
    }

    const firstTableRow = tableRows[0];
    const renderTableHead = () =>
      h(
        'thead',
        {},
        h('tr', {}, next?.(firstTableRow.content as Block[], key, next))
      );

    const tableRowCollection = tableRows
      .slice(1)
      .reduce<(() => any)[]>((tableRowCollection, currentTableRow) => {
        const currentRowCells = currentTableRow.content as Block[];

        if (!currentRowCells || currentRowCells.length === 0) {
          return tableRowCollection;
        }

        const firstRowCell = currentRowCells[0];
        const thFirstRowCell = () =>
          h(
            'th',
            { scope: 'row' },
            next?.(firstRowCell.content as Block[], key, next)
          );

        const restRowCells = currentRowCells.slice(1);
        const tdRestRowCells: (() => any)[] = restRowCells.reduce<
          (() => any)[]
        >(
          (tdRestRowCells, currentElement) => [
            ...tdRestRowCells,
            () =>
              h('td', {}, next?.(currentElement.content as Block[], key, next))
          ],
          []
        );

        return [
          ...tableRowCollection,
          () => h('tr', {}, [thFirstRowCell(), tdRestRowCells.map((x) => x())])
        ];
      }, []);

    const renderTableBody = () =>
      h(
        'tbody',
        {},
        tableRowCollection.map((x) => x())
      );

    return h(
      TableBlock,
      {
        hasLightVerticalHeader: props.hasLightVerticalHeader ?? false,
        cols: props.cols ?? 12,
        colsCentered: props.colsCentered ?? true,
        contentCentered: props.contentCentered ?? false
      },
      () => h('table', {}, [renderTableHead(), renderTableBody()])
    );
  },
  text: (node, key, markRenderer) => {
    if (!node.marks.length) {
      // avoid hydration mismatches
      // XXX: why are "\r" included in response? not normalized in
      //  contentful? "\r" is not rendered on client side.
      const cleanedValue = node.value?.replaceAll('\r', '');

      return cleanedValue === '' &&
        !isChildOf('unordered-list', key ?? '', text.value)
        ? h('br')
        : cleanedValue.split('\n').length > 1
          ? h('span', { key }, convertNewlinesToBreaks(node.value))
          : cleanedValue;
    }

    const marksReversed = [...node.marks].reverse();

    return marksReversed.reduce(
      // XXX: proper typing
      (aggregate: any, mark, i) => {
        if (
          mark.type in markRenderer &&
          typeof markRenderer[mark.type] === 'function'
        ) {
          return markRenderer[mark.type]([aggregate], `${key}-${i}`, h);
        }
        if (node.value) {
          return aggregate;
        }
      },
      node.value
    );
  }
};
</script>
